// Guest share links for one device — the whole lifecycle in one modal: mint a link
// with a TTL, see every link ever minted for the device, and revoke the live ones.
// Opened from the share button in the stream toolbar (inline-stream.ts).
//
// A link opens /s/<token>: the device's screen with no panel login, limited to touch +
// system navigation (+ camera passthrough when enabled). It is a bearer credential, so
// the list doubles as the audit trail — rows are kept after expiry/revoke, showing who
// minted each link, when it was first opened and from which IP.
//
// Camera passthrough exists only on VMOS farms (the box injects the stream into the
// instance's sensor; a physical phone has no equivalent), so `allowCamera` hides the
// checkbox everywhere else. The server forces the flag off there too — hiding it here
// is UX, not the boundary.
import { esc, openModal, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
/** Duration picker units. The amount is a free number, so any TTL inside the
 *  server's 5 min … 7 days window can be dialled in. */
const TTL_UNITS = [
    { key: 'minutes', labelKey: 'devices.ttl_minutes', minutes: 1 },
    { key: 'hours', labelKey: 'devices.ttl_hours', minutes: 60 },
    { key: 'days', labelKey: 'devices.ttl_days', minutes: 60 * 24 },
];
const DEFAULT_TTL_AMOUNT = 1;
const DEFAULT_TTL_UNIT = 'hours';
/** Server bounds, mirrored so the form rejects before the round-trip. */
const MIN_TTL_MINUTES = 5;
const MAX_TTL_MINUTES = 7 * 24 * 60;
/** "in 42 min" / "in 3 h 10 min" / "expired" — the only thing an operator really reads. */
function remaining(expiresAt) {
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0)
        return t('devices.share_expired');
    const totalMin = Math.round(ms / 60000);
    if (totalMin < 60)
        return t('devices.share_in_min', { min: totalMin });
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m ? t('devices.share_in_h_min', { h, min: m }) : t('devices.share_in_h', { h });
}
function formatStamp(value) {
    if (!value)
        return '—';
    const d = new Date(value);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
// Only live links are ever rendered — revoking deletes the row outright, and the
// server drops expired ones when the list is fetched.
function shareRowHtml(s) {
    const opened = s.first_seen_at
        ? `${t('devices.share_opened_at', { time: formatStamp(s.first_seen_at) })}${s.last_ip ? ` · ${esc(s.last_ip)}` : ''}`
        : t('devices.share_never_opened');
    const by = s.created_by_login
        ? t('devices.share_by', { login: esc(s.created_by_login) })
        : t('devices.share_by_deleted_user');
    return `
        <div class="share-row" data-share-id="${s.id}">
            <div class="share-row__main">
                <div class="share-row__title">
                    ${s.label ? esc(s.label) : `link-${s.id}`}
                    <span class="share-row__status">${esc(remaining(s.expires_at))}</span>
                    ${s.camera ? `<span class="share-row__tag">${t('devices.share_camera_tag')}</span>` : ''}
                </div>
                <div class="share-row__meta">${by} · ${formatStamp(s.created_at)} · ${opened}</div>
                ${s.url ? `<div class="share-row__url">${esc(s.url)}</div>` : ''}
            </div>
            <div class="share-row__actions">
                <button type="button" class="tbl-btn" data-share-copy="${s.id}">⎘ ${t('common.copy')}</button>
                <button type="button" class="tbl-btn delete-btn" data-share-revoke="${s.id}">${t('devices.share_revoke')}</button>
            </div>
        </div>
    `;
}
/**
 * Open the share-links manager for a device. `farmId` scopes every call — the API is
 * device-scoped, so an operator only ever sees links for devices they can reach.
 * `allowCamera` (VMOS farms) decides whether the camera-passthrough option is offered.
 */
export function showDeviceShareModal(farmId, udid, deviceName, allowCamera) {
    const modal = openModal(`
        <h3 class="modal__title">${t('devices.share_links_title', { name: esc(deviceName) })}</h3>
        <p class="modal__text">
            ${t('devices.share_intro')}
        </p>
        <div class="share-create">
            <div class="modal__field share-create__ttl">
                <label>${t('devices.share_valid_for')}</label>
                <div class="share-ttl-picker">
                    <input type="number" class="settings-input" data-share-ttl-amount
                           min="1" step="1" value="${DEFAULT_TTL_AMOUNT}" inputmode="numeric" />
                    <select class="settings-input" data-share-ttl-unit>
                        ${TTL_UNITS.map((u) => `<option value="${u.key}"${u.key === DEFAULT_TTL_UNIT ? ' selected' : ''}>${t(u.labelKey)}</option>`).join('')}
                    </select>
                </div>
            </div>
            <div class="modal__field">
                <label>${t('devices.share_note')}</label>
                <input type="text" class="settings-input" data-share-label maxlength="64" autocomplete="off" placeholder="${t('devices.share_note_placeholder')}" />
            </div>
            ${allowCamera ? `
            <label class="share-create__check">
                <input type="checkbox" data-share-camera checked /> ${t('devices.share_allow_camera')}
            </label>` : ''}
            <button type="button" class="modal__submit" data-share-create>${t('devices.share_create')}</button>
        </div>
        <div class="settings-error modal__error" data-share-err style="display:none"></div>
        <div class="share-list" data-share-list><p class="modal__text">${t('common.loading')}</p></div>
    `);
    // The links list needs the room: a URL, its metadata and three actions per row.
    modal.classList.add('modal--wide');
    const list = modal.querySelector('[data-share-list]');
    const err = modal.querySelector('[data-share-err]');
    const base = `/api/farms/${farmId}/devices/${encodeURIComponent(udid)}/shares`;
    let shares = [];
    const render = () => {
        list.innerHTML = shares.length
            ? shares.map(shareRowHtml).join('')
            : `<p class="modal__text">${t('devices.share_none')}</p>`;
    };
    const load = async () => {
        try {
            const res = await fetch(base);
            if (!res.ok)
                throw new Error((await res.json()).error ?? t('devices.share_failed_load'));
            shares = (await res.json()).shares;
            render();
        }
        catch (e) {
            list.innerHTML = '';
            showModalErr(err, e.message);
        }
    };
    modal.querySelector('[data-share-create]').addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const amount = Number(modal.querySelector('[data-share-ttl-amount]').value);
        const unitKey = modal.querySelector('[data-share-ttl-unit]').value;
        const unit = TTL_UNITS.find((u) => u.key === unitKey) ?? TTL_UNITS[1];
        const ttl = Math.round(amount * unit.minutes);
        if (!Number.isFinite(ttl) || ttl < MIN_TTL_MINUTES || ttl > MAX_TTL_MINUTES) {
            showModalErr(err, t('devices.share_ttl_range', { min: MIN_TTL_MINUTES }));
            return;
        }
        const label = modal.querySelector('[data-share-label]').value.trim();
        const camera = allowCamera
            && (modal.querySelector('[data-share-camera]')?.checked ?? false);
        btn.disabled = true;
        err.style.display = 'none';
        try {
            const res = await fetch(base, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ttlMinutes: ttl, label, camera }),
            });
            const body = await res.json();
            if (!res.ok || !body.share)
                throw new Error(body.error ?? t('devices.share_failed_create'));
            shares = [body.share, ...shares];
            render();
            // Straight to the clipboard: the link is almost always created to be sent
            // somewhere immediately.
            if (body.share.url)
                void navigator.clipboard.writeText(body.share.url).catch(() => { });
            modal.querySelector('[data-share-label]').value = '';
        }
        catch (e2) {
            showModalErr(err, e2.message);
        }
        finally {
            btn.disabled = false;
        }
    });
    list.addEventListener('click', async (e) => {
        const target = e.target;
        const copyId = target.closest('[data-share-copy]')?.dataset['shareCopy'];
        const revokeId = target.closest('[data-share-revoke]')?.dataset['shareRevoke'];
        if (copyId) {
            const share = shares.find((s) => s.id === Number(copyId));
            if (share?.url) {
                void navigator.clipboard.writeText(share.url);
                const btn = target.closest('[data-share-copy]');
                btn.textContent = `✓ ${t('common.copied')}`;
                setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
            }
            return;
        }
        if (revokeId) {
            const btn = target.closest('[data-share-revoke]');
            btn.disabled = true;
            try {
                const res = await fetch(`${base}/${revokeId}`, { method: 'DELETE' });
                if (!res.ok)
                    throw new Error((await res.json()).error ?? t('devices.share_failed_revoke'));
                await load();
            }
            catch (e2) {
                btn.disabled = false;
                showModalErr(err, e2.message);
            }
        }
    });
    void load();
}
